import type {
  AdminUser,
  AssistantResponse,
  AuditEvent,
  AuthenticatedSessionResponse,
  AuthSessionResponse,
  DashboardData,
  Property,
  Recommendation,
  ScenarioInputs,
  ScenarioResult,
  UserStatus,
  ControlsData,
  DecisionReceipt,
  InsightsData,
  LeadsData,
  RecommendationDecisionInput,
  SearchResponse,
  TodayData,
  ToursData,
} from "../types";

let csrfToken = "";

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function setCsrfToken(value: string | null) {
  csrfToken = value ?? "";
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body) headers.set("Content-Type", "application/json");
  if (init?.method && !["GET", "HEAD"].includes(init.method) && csrfToken) {
    headers.set("X-YieldBoost-CSRF", csrfToken);
  }

  const response = await fetch(`/api${path}`, {
    credentials: "same-origin",
    ...init,
    headers,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    if (
      response.status === 401 &&
      path !== "/auth/session" &&
      path !== "/auth/login"
    ) {
      window.dispatchEvent(new Event("yieldboost:unauthorized"));
    }
    throw new ApiError(
      payload?.error ?? `Request failed with ${response.status}`,
      response.status,
      payload?.code,
    );
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export interface RegistrationInput {
  displayName: string;
  email: string;
  company: string;
  jobTitle: string;
  password: string;
}

export const api = {
  session: () => request<AuthSessionResponse>("/auth/session"),
  login: (email: string, password: string) =>
    request<AuthenticatedSessionResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  register: (input: RegistrationInput) =>
    request<{ message: string }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ message: string }>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  adminUsers: () => request<AdminUser[]>("/admin/users"),
  setUserStatus: (id: string, status: Extract<UserStatus, "approved" | "suspended">) =>
    request<AdminUser>(`/admin/users/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),
  dashboard: () => request<DashboardData>("/dashboard"),
  today: () => request<TodayData>("/v1/today"),
  leads: () => request<LeadsData>("/v1/leads"),
  takeOverLead: (id: string) =>
    request<{ message: string }>(`/v1/leads/${encodeURIComponent(id)}/take-over`, { method: "POST" }),
  tours: () => request<ToursData>("/v1/tours"),
  insights: () => request<InsightsData>("/v1/insights"),
  controls: () => request<ControlsData>("/v1/controls"),
  search: (query: string) => request<SearchResponse>(`/search?q=${encodeURIComponent(query)}`),
  properties: () => request<Property[]>("/properties"),
  property: (id: string) =>
    request<{ property: Property; recommendations: Recommendation[] }>(
      `/properties/${id}`,
    ),
  recommendations: () => request<Recommendation[]>("/recommendations"),
  recommendation: (id: string) =>
    request<{ recommendation: Recommendation; history: AuditEvent[] }>(
      `/recommendations/${id}`,
    ),
  decideRecommendation: (id: string, input: RecommendationDecisionInput) =>
    request<{ decision: RecommendationDecisionInput & { id: string; decidedAt: string }; receipt: DecisionReceipt }>(
      `/v1/recommendations/${encodeURIComponent(id)}/decisions`,
      { method: "POST", body: JSON.stringify(input) },
    ),
  decisionReceipt: (id: string) =>
    request<DecisionReceipt>(`/v1/decision-receipts/${encodeURIComponent(id)}`),
  audit: () => request<AuditEvent[]>("/audit"),
  scenario: (inputs: ScenarioInputs) =>
    request<ScenarioResult>("/scenario", {
      method: "POST",
      body: JSON.stringify(inputs),
    }),
  assistant: (question: string) =>
    request<AssistantResponse>("/assistant", {
      method: "POST",
      body: JSON.stringify({ question }),
    }),
};
